import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {SearchObjectResult} from '../../shared/search-object-result.model';
import {HttpUtils} from '../../shared/http-utils';
import {URL_BASE} from '../../core/constants';
import {map, mergeMap} from 'rxjs/operators';
import {OverdueDeliveryCriteria} from './overdue-delivery-criteria.model';
import {OverdueCollectionProductType, OverdueCollectionType, OverdueDelivery} from './overdue-delivery.model';
import {OverdueMetrics} from './overdue-metrics.model';
import {
  OverdueLoadAction,
  OverdueLoadActionRequest,
  OverdueLoadActionStatus,
  OverdueLoadActionType
} from './overdue-load-action.model';
import {FileService} from '../../shared/services/file.service';
import {Moment} from 'moment';

@Injectable({
  providedIn: 'root'
})
export class OverdueDeliveryService {

  private OVERDUE_DELIVERIES_URI = 'overdue/deliveries';

  private OVERDUE_METRICS_URI = 'overdue/metrics';

  private OVERDUE_ACTIONS_URI = 'overdue/actions'

  constructor(
    private http: HttpClient,
    private fileService: FileService
  ) {}

  public searchOverdueDeliveries(
    page: number, size: number, criteria: OverdueDeliveryCriteria, isGlobalUser: boolean
  ): Observable<SearchObjectResult<OverdueDelivery>> {
    const { headers, params } = this.prepareSearchRequest(criteria, page, size);

    const url = `${URL_BASE}/api/${this.OVERDUE_DELIVERIES_URI}/${isGlobalUser ? 'manager' : 'carrier'}/search`;
    return this.http.get(url, { headers, params })
      .pipe(map(result => SearchObjectResult.fromDto(result, OverdueDelivery.fromDto)));
  }

  public searchDeliveriesByAction(
    p: number,
    pSize: number,
    criteria: any,
    lastActionAfter: Moment,
    lastActionBefore: Moment,
    lastActionType: string[],
    isGlobalUser: boolean
  ): Observable<SearchObjectResult<OverdueDelivery>> {
    const headers = HttpUtils.createHeaders();
    const params = {
      page: String(p),
      size: String(pSize),
      ...this.prepareSearchActionsRequest(criteria, lastActionAfter, lastActionBefore, lastActionType),
      isGlobalUser: String(isGlobalUser)
    };

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/search`;
    return this.http.get(url, {headers, params})
      .pipe(map(result => SearchObjectResult.fromDto(result, OverdueDelivery.fromDto)));
  }

  public exportDeliveries(criteria: any): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const params = this.prepareCriteria(criteria);

    const url = `${URL_BASE}/api/${this.OVERDUE_DELIVERIES_URI}/export`;
    return this.http.get(url, { headers, params, responseType: 'blob', observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }

  public exportActions(criteria: any, lastActionAfter: Moment, lastActionBefore: Moment, lastActionType: string[]) {
    const headers = HttpUtils.createHeaders();
    const params = this.prepareSearchActionsRequest({...criteria}, lastActionAfter, lastActionBefore, lastActionType);

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/export`;
    return this.http.get(url, { headers, params, responseType: 'blob', observe: 'response' })
      .pipe(
        mergeMap((response: HttpResponse<Blob>) => this.fileService.saveToFileSystem(response))
      );
  }

  public getMetrics(criteria: any): Observable<OverdueMetrics> {
    const headers = HttpUtils.createHeaders();
    const params = this.prepareCriteria(criteria);

    const url = `${URL_BASE}/api/${this.OVERDUE_METRICS_URI}`;
    return this.http.get(url, {headers, params})
      .pipe(map(result => OverdueMetrics.fromDto(result)));
  }

  private prepareSearchRequest(criteria: any, page: number, size: number) {
    criteria = this.prepareCriteria(criteria);

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      ...criteria,
    };
    return { headers, params };
  }

  private prepareSearchActionsRequest(criteria: any, lastActionAfter: Moment, lastActionBefore: Moment, lastActionType: string[]) {
    const request = {
      ...this.prepareMainCriteria(criteria),
      lastActionAfter: lastActionAfter ? lastActionAfter.toISOString() : null,
      lastActionBefore: lastActionBefore ? lastActionBefore.toISOString() : null,
      lastActionTypes: lastActionType ? lastActionType : null
    };

    if (!lastActionAfter) {
      delete request['lastActionAfter'];
    }
    if (!lastActionBefore) {
      delete request['lastActionBefore'];
    }
    if (!lastActionType) {
      delete request['lastActionType'];
    }

    return request;
  }

  private prepareCriteria(criteria: any) {

    const request = this.prepareMainCriteria(criteria);

    if (criteria.shipmentNumber && criteria.shipmentNumber.length > 0) {
      request.shipmentNumber = criteria.shipmentNumber.join(',');
    }

    if (criteria.originCity) {
      request.originCity = criteria.originCity;
    }

    if (criteria.originPostalCode) {
      request.originPostalCode = criteria.originPostalCode;
    }

    if (criteria.destinationCity) {
      request.destinationCity = criteria.destinationCity;
    }

    if (criteria.destinationPostalCode) {
      request.destinationPostalCode = criteria.destinationPostalCode;
    }

    if (criteria.productType && criteria.productType !== OverdueCollectionProductType.ALL) {
      request.productType = criteria.productType;
    }

    if (criteria.collectionType && criteria.collectionType !== OverdueCollectionType.BOTH) {
      request.collectionType = criteria.collectionType;
    }

    if (criteria.pickCompleted !== null && criteria.pickCompleted !== OverdueCollectionType.BOTH) {
      request.pickCompleted = criteria.pickCompleted;
    }

    if (criteria.blocked !== null && criteria.blocked !== OverdueCollectionType.BOTH) {
      request.blocked = criteria.blocked;
    }

    if (criteria.sot !== null && criteria.sot !== OverdueCollectionType.BOTH) {
      request.sot = criteria.sot;
    }

    if (criteria.carrierAssigned !== null && criteria.carrierAssigned !== OverdueCollectionType.BOTH) {
      request.carrierAssigned = criteria.carrierAssigned;
    }

    if (criteria.deliveryDateBefore) {
      request.deliveryDateBefore = criteria.deliveryDateBefore.format('YYYY-MM-DD');
    }

    if (criteria.deliveryDateAfter) {
      request.deliveryDateAfter = criteria.deliveryDateAfter.format('YYYY-MM-DD');
    }

    if (criteria.deliveryDeadlineBefore) {
      request.deliveryDeadlineBefore = criteria.deliveryDeadlineBefore.format('YYYY-MM-DD');
    }

    if (criteria.deliveryDeadlineAfter) {
      request.deliveryDeadlineAfter = criteria.deliveryDeadlineAfter.format('YYYY-MM-DD');
    }

    if (criteria.carrierScacCode) {
      request.carrierScacCode = criteria.carrierScacCode;
    }
    return request;
  }

  private prepareMainCriteria(criteria: any) {
    const request: any = {};
    if (criteria.deliveryNumber && criteria.deliveryNumber.length > 0) {
      request.deliveryNumber = criteria.deliveryNumber.join(',');
    }

    if (criteria.originGlid && criteria.originGlid.length > 0) {
      request.originGlid = criteria.originGlid.join(',').toUpperCase();
    }

    if (criteria.originCountryCode) {
      request.originCountryCode = criteria.originCountryCode;
    }

    if (criteria.destinationGlid && criteria.destinationGlid.length > 0) {
      request.destinationGlid = criteria.destinationGlid.join(',').toUpperCase();
    }

    if (criteria.destinationCountryCode) {
      request.destinationCountryCode = criteria.destinationCountryCode;
    }

    if (criteria.carrierVendorNumbers) {
      request.carrierVendorNumbers = criteria.carrierVendorNumbers;
    }
    return request;
  }

  fetchHistory(deliveryNumber: string): Observable<OverdueLoadAction[]> {
    const headers = HttpUtils.createHeaders();

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/${deliveryNumber}`;
    return this.http.get(url, {headers})
      .pipe(map((history: any[]) => history.map(action => OverdueLoadAction.fromDto(action))));
  }

  fetchLastActionToReview(deliveryNumber: string): Observable<OverdueLoadAction> {
    const headers = HttpUtils.createHeaders();

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/${deliveryNumber}/to/review`;
    return this.http.get(url, {headers})
      .pipe(map(res => OverdueLoadAction.fromDto(res)))
  }

  hasMultipleLastActionToReview(deliveriesNumber: string[]): Observable<boolean> {
    const headers = HttpUtils.createHeaders();

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/multiple/to/review`;
    return this.http.put(url, deliveriesNumber, {headers})
      .pipe(map(res => Boolean(res)))
  }

  sendActionRequest(isGlobalUser: boolean, data: OverdueLoadActionRequest): Observable<any> {
    const actionRequest = this.fillMissingOverdueLoadActionRequestFields(isGlobalUser, data);
    const headers = HttpUtils.createHeaders();
    const params = { isGlobalUser: String(isGlobalUser) };
    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}`;
    return this.http.post(url, actionRequest, {headers, params});
  }

  sendMultiActionRequest(isGlobalUser: boolean, datas: OverdueLoadActionRequest[]): Observable<any> {
    const actionRequests = datas.map(data => this.fillMissingOverdueLoadActionRequestFields(isGlobalUser, data));
    const headers = HttpUtils.createHeaders();
    const params = { isGlobalUser: String(isGlobalUser) };
    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/multiple`;
    return this.http.post(url, actionRequests, {headers, params});
  }

  sendReviewRequest(data: any) {
    const headers = HttpUtils.createHeaders();

    const url = `${URL_BASE}/api/${this.OVERDUE_ACTIONS_URI}/review`;
    return this.http.post(url, data, {headers});
  }

  private fillMissingOverdueLoadActionRequestFields(isGlobalUser: boolean, actionRequest: OverdueLoadActionRequest) {
    const result = OverdueLoadActionRequest.fromActionRequest(actionRequest);
    if (isGlobalUser) {
      result.status = OverdueLoadActionStatus.APPROVED;
      if (result.type === OverdueLoadActionType.INFO_REQUEST) {
        result.status = OverdueLoadActionStatus.PENDING;
      } else if (result.type === OverdueLoadActionType.OTHER_REVIEW) {
        result.chep_commentary = result.haulier_commentary;
        result.haulier_commentary = null;
      }
    }

    return result;
  }
}
